// >>> **THE LADDER CARD'S TYPE LIVES WITH THE PAYLOAD IT DESCRIBES, WHICH IS WHERE IT ALWAYS
// BELONGED.** <<< It sat in the component that USED to render it (deleted 2026-08-18 with the popup),
// and this module imported it BACKWARDS -- the law reaching into a component for the shape of a
// SERVER response. Her 2026-08-18 ruling retires that component, and a type is a different fact
// from a renderer (R18): the popup is gone and the ladder payload is exactly as it was.
export type TierCard = {
  tier: string;
  label: string;
  price: number | null;
  priceText: string;
  period: string;
  tagline: string;
  inherits: string | null;
  inheritsLabel: string | null;
  unlocks: Array<{ key: string; label: string }>;
  lalaTurnsPerDay: number;
};

// **THE SERVER'S LOCK PAYLOAD** (T8-2/T8-3: *the client never decides this — it is told*), moved out
// of App.tsx by R73 because it is the OTHER half of the catalogue this module reads: a refusal
// carries the ladder, and a box opened from a button fetches it. One payload, one file.
//
// W6-2: the cap fields are GONE from LockInfo. A capped response no longer carries a lock at all
// (Lala says it in chat, in her voice), so `capped`, `capPerDay`, `notice` and `resetsAt` had no
// sender left, and a type that still describes a payload nobody sends is how the next agent rebuilds
// the popup.
export type LockInfo = {
  feature?: string;
  // ENTITLEMENT-UNREADABLE-Q1 (her YES, 2026-10-12): the house could not read her membership, so this is not a refusal.
  unread?: boolean;
  featureLabel?: string;
  needsTier?: string | null;
  tier?: string;
  ladder?: TierCard[];
  freeLayer?: { unlocks: Array<{ key: string; label: string }> };
  // L15 — **TWO FACTS, AND ONLY ONE OF THEM IS A TIER (R18).** `tier` is what she may open right now,
  // which for a frozen member is `free` and is what every gate reads; `frozenTier` is what she is one
  // tap away from reopening. The refusal carries both because it is the ONE payload T8-3's
  // interceptor turns into her purple box, and a member who has been paying all year must not be told
  // to CHOOSE a membership.
  frozen?: boolean;
  frozenTier?: string | null;
  // WALL-1 — **THE GIFT IS THE SAME TWO FACTS AS THE FREEZE (R18).** `tier` is what she may open
  // right now, which for a gifted Apprentice really is `apprentice`; `giftTier` is the membership
  // she HOLDS and is not being charged for. The refusal carries both because a member handed a
  // complimentary membership and then told to CHOOSE one reads it as a glitch, and three tickets in
  // one week said exactly that.
  gift?: boolean;
  giftTier?: string | null;
};

// R73 — THE ONE PAGE THAT ASKS HER FOR MONEY, WITH NOTHING ON IT TO BUY.
//
// `/api/membership/tiers` has THREE call sites and **none of them forges anything** — which is
// exactly why two censuses and R71's own sweep walked past it. Each is
// `r.ok ? r.json() : null` guarded by `if (!d) return`, so a failed read is not a fabricated empty
// list, **it is nothing happening at all.** And nothing happening is what `useState<TierCard[]>([])`
// already looks like.
//
// **SO THE MONEY PAGE DRAWS ITS FRAME, ITS EYEBROW, ITS HEADING AND ITS LEDE — *Choose your
// membership* — AND THEN NO CARDS.** R58's absent section, on the one screen where the absence costs
// a sale rather than a reading. The locked door is the same box: a member refused at a Vet door and
// sent here to fix it meets a page with no way to fix it, and nothing is red anywhere.
//
// **THE ANSWER IS `T[] | null`, NOT THE FOUR-WAY ONE, AND THAT IS ARGUED (R58's ruling).** This route
// answers out of `server/tiers.js` — `ladder()`, `freeLayer()` and `ONE_TIME_PRODUCTS` are
// synchronous reads of committed data with no database, no session and no `await` between them, so
// **it cannot refuse.** A `sign-in` or `refused` branch here would be a second answer key for
// something the route has no way of saying. What it can be is unreachable, and that is `null`.
//
// **ONE READ FOR ALL THREE, BECAUSE THE THREE COPIES HAD ALREADY DRIFTED** (R31/R60): the money page
// reads `ladder` AND `oneTime`, the locked door reads only `ladder`, and the Library reads only
// `oneTime`. Three transcriptions of one payload is three places for the next field to be forgotten.
//
// `doFetch` is a PARAMETER (R46), so both answers are reachable with no browser and no server.
// **THE ONE-TIME PRODUCT'S SHAPE, DECLARED ONCE** — it was typed inline at BOTH surfaces that hold
// one (the money page and the Library), which is R31's crossing in miniature: two transcriptions of
// one payload, and the next field added to it would have reached one of them.
export type OneTimeProduct = {
  key: string;
  label: string;
  priceText: string;
  blurb: string;
  videoSlug?: string;
};

export type TierCatalog<L, P> = {
  ladder: L[];
  freeLayer: unknown | null;
  oneTime: P[];
};

// >>> **THE CATALOGUE AS THE MEMBERSHIP SURFACES READ IT.** <<< It moved here from
// `MembershipValueStack` on 2026-08-27, when her ruling retired that component with the artwork
// page it belonged to. **A TYPE IS A DIFFERENT FACT FROM A RENDERER (R18)** — this is the shape of
// the payload this module already fetches, so it belongs beside the transport rather than beside
// whichever component happens to draw it this month. That is the same move `TierCard` and
// `LockInfo` each already made, for the same reason.
export type ValueCatalog = TierCatalog<TierCard, unknown>;

export const tierCatalogPath = "/api/membership/tiers";

// `null` is "we could not read the catalogue", and it is deliberately NOT an empty catalogue: this
// house sells three tiers and the list is committed data, so an EMPTY ladder from a landed read
// would itself be a defect worth seeing rather than a state to paint over (R18's distinction, made
// about a list that can never legitimately be empty).
export async function readTierCatalog<L, P>(
  doFetch: typeof fetch = typeof fetch === "function" ? fetch : (undefined as never),
): Promise<TierCatalog<L, P> | null> {
  let response: Response;
  try {
    // `no-store`: prices are the one thing on this site a stale cache must never answer.
    response = await doFetch(tierCatalogPath, { cache: "no-store" });
  } catch {
    return null;
  }
  const data = await response.json().catch(() => null) as
    | { ladder?: unknown; freeLayer?: unknown; oneTime?: unknown }
    | null;
  // PRESENCE, never truthiness (R53): the LADDER is what makes this a catalogue, so a 200 carrying
  // no ladder array is a read that did not land, whatever else rode with it.
  if (!response.ok || !data || !Array.isArray(data.ladder)) return null;
  return {
    ladder: data.ladder as L[],
    freeLayer: data.freeLayer ?? null,
    oneTime: Array.isArray(data.oneTime) ? (data.oneTime as P[]) : [],
  };
}

// **THE SENTENCE IS DRAWN ONLY WHERE THE CARDS WOULD HAVE BEEN**, and only once the read has
// actually failed — a page that says this while the request is still in flight is a page that
// flashes a failure at every member who opens it (W5-2/R39's third state, said about a sentence).
// **THREE VALUES, BECAUSE TWO CANNOT SAY IT** (W5-2/R39's third state, ITEM 7's `self` argument):
// `undefined` is "the answer has not come back", `null` is "it came back and we could not read the
// catalogue", and a catalogue is a catalogue. Only the middle one has anything to say.
export function tierCatalogNote(answer: TierCatalog<unknown, unknown> | null | undefined, failed: string) {
  return answer === null ? failed : "";
}
